/** The tour operator's Pre-check-in tab (backend plan 2026-10-08-console-precheck-in-editor,
 *  Section 2 "P2 — console API"):
 *
 *  - `GET /tour_operators/{id}/pre_check_settings/` (`console_top_view`): the operator's
 *    settings (`stored: false` = the defaults, nothing saved yet), its two field lists, the
 *    waiver rows, its custom field definitions (with who uses them and the answers stored),
 *    the options the editor and the guest-form preview need (waivers, tag categories,
 *    template variables), which tours it reaches, and a version token.
 *  - `PUT` (`console_top_manage`): `{settings, fields: {lead, party}, waivers, definitions,
 *    version, reason}` — every key required, the whole form replaced; answers with the GET
 *    shape. 409 `pre_check_modified` when `version` is stale (nothing written); 400
 *    `{detail, errors: {"<path>": "message"}}` (paths: `pre_check_settings.<key>`, the two
 *    field lists, `pre_check_settings.waivers`, `definitions`, `definitions.<id>`,
 *    `definitions.<id>.name|type`, `fields`, `version`, `reason`).
 *
 *  Shapes come from the generated spec types; never hand-mirror them. */
import { apiGet, apiPut, ConsoleApiError } from './client';
import type { components } from './types';

type Schemas = components['schemas'];

export type OperatorPreCheck = Schemas['ConsoleOperatorPreCheckSettings'];
export type OperatorPreCheckWrite = Schemas['ConsoleOperatorPreCheckSettingsWrite'];
export type OperatorPreCheckValues = Schemas['ConsoleOperatorPreCheckSettingValues'];
export type OperatorPreCheckField = Schemas['ConsoleOperatorPreCheckField'];
export type OperatorPreCheckFieldLists = Schemas['ConsoleOperatorPreCheckFieldLists'];
export type OperatorPreCheckWaiverRow = Schemas['ConsoleOperatorPreCheckWaiverRow'];
export type OperatorPreCheckDefinition = Schemas['ConsoleOperatorPreCheckDefinition'];
export type OperatorPreCheckDefinitionWrite = Schemas['ConsoleOperatorPreCheckDefinitionWrite'];
export type OperatorPreCheckOptions = Schemas['ConsoleOperatorPreCheckOptions'];
export type OperatorPreCheckWaiverOption = Schemas['ConsoleOperatorPreCheckWaiverOption'];
export type OperatorPreCheckCategoryOption = Schemas['ConsoleOperatorPreCheckCategoryOption'];
export type OperatorPreCheckVariables = OperatorPreCheckOptions['variables'];
export type OperatorPreCheckReach = Schemas['ConsoleOperatorPreCheckReach'];
export type OperatorPreCheckErrors = Schemas['ConsoleOperatorPreCheckErrors'];

/** Under the operator, so `['tourOperators']` invalidations reach it. */
export const operatorPreCheckKey = (topId: number) => ['tourOperators', topId, 'preCheckSettings'] as const;

const url = (topId: number) => `/tour_operators/${topId}/pre_check_settings/`;

export function getOperatorPreCheck(topId: number, signal?: AbortSignal): Promise<OperatorPreCheck> {
  return apiGet<OperatorPreCheck>(url(topId), { signal });
}

export function putOperatorPreCheck(topId: number, body: OperatorPreCheckWrite): Promise<OperatorPreCheck> {
  return apiPut<OperatorPreCheck>(url(topId), body);
}

/** Someone saved the operator's pre-check-in settings (or a tour relabelled one of its
 *  custom fields) after `version` was read: 409 `pre_check_modified`, nothing written. Not
 *  a type predicate (see `isStatementTimeout`). */
export function isPreCheckModified(e: unknown): boolean {
  return e instanceof ConsoleApiError && e.status === 409 && e.body?.code === 'pre_check_modified';
}

/** The PUT's refusal (400 `{detail, errors}`), or null for any other failure. */
export function preCheckErrors(e: unknown): OperatorPreCheckErrors | null {
  if (!(e instanceof ConsoleApiError) || e.status !== 400) return null;
  const body = e.body as Partial<OperatorPreCheckErrors> | undefined;
  if (!body || typeof body !== 'object') return null;
  const errors =
    body.errors && typeof body.errors === 'object' && !Array.isArray(body.errors)
      ? Object.fromEntries(Object.entries(body.errors).filter((kv): kv is [string, string] => typeof kv[1] === 'string'))
      : {};
  if (typeof body.detail !== 'string' && Object.keys(errors).length === 0) return null;
  return { detail: typeof body.detail === 'string' ? body.detail : '', errors };
}
